import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Flame, Clock, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

function streakTierClass(streak) {
  if (streak >= 30) return 'streak-electric';
  if (streak >= 15) return 'streak-purple-fire';
  if (streak >= 10) return 'streak-red-fire';
  return 'streak-orange';
}

function streakIcon(streak) {
  if (streak >= 30) return Zap;
  return Flame;
}

export default function DailyQuestionsButton({ variant = 'cyber' }) {
  const { user, API } = useApp();
  const [status, setStatus] = useState(null);
  const [remaining, setRemaining] = useState('');
  const nav = useNavigate();

  const fetchStatus = async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/daily/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('pp-token') || ''}` },
      });
      setStatus(r.data);
    } catch { setStatus(null); }
  };

  useEffect(() => { fetchStatus(); }, [user]);

  useEffect(() => {
    if (!status?.completed || !status?.next_available_at) return;
    const tick = () => {
      const diff = new Date(status.next_available_at) - new Date();
      if (diff <= 0) { fetchStatus(); return; }
      const h = String(Math.floor(diff / 3600000)).padStart(2, '0');
      const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, '0');
      const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, '0');
      setRemaining(`${h}:${m}:${s}`);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [status]);

  if (!user) {
    return (
      <button data-testid="daily-signin-required" onClick={() => nav('/auth')}
        className={variant === 'cyber'
          ? 'px-6 py-4 border border-[#00FFD1]/40 rounded-2xl text-[#00FFD1] font-orbitron tracking-widest'
          : 'storybook-btn px-6 py-4 rounded-2xl'}>
        Sign in to unlock Daily Quest
      </button>
    );
  }

  const completed = status?.completed;
  const streak = status?.streak || 0;
  const tier = streakTierClass(streak);
  const StreakIcon = streakIcon(streak);

  if (variant === 'storybook') {
    return (
      <motion.button
        data-testid="daily-questions-btn"
        disabled={completed}
        onClick={() => nav('/daily')}
        whileHover={!completed ? { y: -3 } : {}}
        className={`relative w-full px-6 py-5 rounded-2xl text-left flex items-center gap-4 ${
          completed
            ? 'bg-[#EFE3C6] border-2 border-[#B9A97C] text-[#68615A] cursor-not-allowed'
            : 'storybook-btn'
        }`}
      >
        {completed ? <Clock size={28} /> : <StreakIcon size={28} className="text-[#C15A00]" />}
        <div>
          <div className="font-storybook text-2xl">Daily Quest of the Kingdom</div>
          <div className="text-sm mt-0.5 opacity-80">
            {completed ? `Return in ${remaining}` : `5 questions await, brave scholar`}
          </div>
          {streak > 0 && (
            <div data-testid="daily-streak-badge" className="mt-1 text-xs font-body-story italic text-[#8C1C13]">
              {streak}-day streak · keep it alive!
            </div>
          )}
        </div>
      </motion.button>
    );
  }

  // cyber
  return (
    <motion.button
      data-testid="daily-questions-btn"
      disabled={completed}
      onClick={() => nav('/daily')}
      whileHover={!completed ? { scale: 1.02 } : {}}
      className={`relative w-full px-6 py-5 rounded-2xl text-left flex items-center gap-4 border ${
        completed
          ? 'bg-[#1a1a1a] border-neutral-800 text-neutral-500 cursor-not-allowed'
          : `${tier} text-white`
      }`}
    >
      {completed
        ? <Clock size={28} className="text-neutral-500" />
        : <StreakIcon size={28} className={streak >= 30 ? 'text-blue-200' : streak >= 15 ? 'text-fuchsia-200' : streak >= 10 ? 'text-red-200' : 'text-[#FF8A00]'} />}
      <div className="relative z-[1]">
        <div className="font-orbitron text-xl tracking-wider">
          {completed ? 'DAILY_QUEST // LOCKED' : 'DAILY_QUEST // ACTIVE'}
        </div>
        <div className="font-mono-cyber text-xs mt-1 opacity-90">
          {completed ? `next_unlock: ${remaining}` : '5 questions // stream awaiting'}
        </div>
        {streak > 0 && (
          <div data-testid="daily-streak-badge"
            className={`font-mono-cyber text-xs mt-1 font-bold ${
              streak >= 30 ? 'text-blue-200' : streak >= 15 ? 'text-fuchsia-200' : streak >= 10 ? 'text-red-200' : 'text-[#FF8A00]'
            }`}>
            STREAK: {streak}d {streak >= 30 ? '// ELECTRIC' : streak >= 15 ? '// PURPLE INFERNO' : streak >= 10 ? '// RED FIRE' : ''}
          </div>
        )}
      </div>
    </motion.button>
  );
}
